import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Button,
  Menu,
  MenuItem,
  Checkbox,
  ListItemText,
  TextField,
  CircularProgress,
  Divider,
  Tooltip,
  Snackbar,
  Alert,
  useTheme,
  Typography,
} from "@mui/material";
import { ArrowDropDown } from "@mui/icons-material";
import { useCompanies } from "../hooks/useCompanies";
import { useTranslation } from "react-i18next";

const CompanySelector = ({
  placeholder,
  maxDisplayNames = 2,
  workspaceLabel,
  /** Slug do workspace (mesmo critério do header Company); separado do label de UI para mobile. */
  workspaceTenantKey = "",
}) => {
  const { t } = useTranslation("shell");
  const theme = useTheme();
  const selectPh = placeholder ?? t("company.selectPlaceholder");

  const tenantKey = String(workspaceTenantKey || workspaceLabel || "").trim();

  const [anchorEl, setAnchorEl] = useState(null);

  // Persistido (escopo atual da aplicação)
  const [selectedCompanyIds, setSelectedCompanyIds] = useState(() => {
    const saved = sessionStorage.getItem("selectedCompanies");
    return saved ? JSON.parse(saved) : [];
  });

  // Draft (edição local)
  const [draftCompanyIds, setDraftCompanyIds] = useState([]);

  const [hasChanges, setHasChanges] = useState(false);
  const [showWarning, setShowWarning] = useState(false);
  const [filterText, setFilterText] = useState("");

  const { data, isLoading, isError, refetch } = useCompanies(1, 100, filterText, tenantKey);

  // Trocar de workspace (ou limpar storage no console) sem desmontar o MainLayout: re-hidratar seleção do session ou zerar.
  useEffect(() => {
    let saved = [];
    try {
      const raw = sessionStorage.getItem("selectedCompanies");
      saved = raw ? JSON.parse(raw) : [];
    } catch {
      saved = [];
    }
    setSelectedCompanyIds(Array.isArray(saved) ? saved : []);
  }, [tenantKey]);

  const companies = useMemo(
    () => data?.companies || [],
    [data?.companies]
  );

  // IDs de todas carregadas
  const allCompanyIds = useMemo(
    () => companies.map((c) => c.id),
    [companies]
  );

  // Estados do checkbox mestre (baseado no draft)
  const allSelected = useMemo(
    () =>
      allCompanyIds.length > 0 &&
      draftCompanyIds.length === allCompanyIds.length,
    [allCompanyIds, draftCompanyIds]
  );

  const someSelected = useMemo(
    () =>
      draftCompanyIds.length > 0 &&
      draftCompanyIds.length < allCompanyIds.length,
    [allCompanyIds, draftCompanyIds]
  );

  // Selecionar todas na primeira carga se não houver sessão
  useEffect(() => {
    if (!selectedCompanyIds.length && companies.length) {
      setSelectedCompanyIds(allCompanyIds);
      sessionStorage.setItem(
        "selectedCompanies",
        JSON.stringify(allCompanyIds)
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companies]);

  // IDs selecionados que não existem na lista atual (outro tenant / cache antigo): corrigir sem reload.
  useEffect(() => {
    if (!companies.length || !selectedCompanyIds.length) return;
    const idSet = new Set(companies.map((c) => c.id));
    const valid = selectedCompanyIds.filter((id) => idSet.has(id));
    if (valid.length === selectedCompanyIds.length) return;
    const next = valid.length ? valid : allCompanyIds;
    setSelectedCompanyIds(next);
    try {
      sessionStorage.setItem("selectedCompanies", JSON.stringify(next));
    } catch {
      /* ignore */
    }
  }, [companies, allCompanyIds, selectedCompanyIds]);

  // ===== MENU =====

  const handleOpenMenu = (event) => {
    setAnchorEl(event.currentTarget);
    setDraftCompanyIds(selectedCompanyIds);
    setHasChanges(false);
  };

  const handleCloseMenu = () => {
    if (hasChanges) {
      setShowWarning(true);
    }
    setAnchorEl(null);
  };

  // ===== TOGGLES =====

  const handleToggleCompany = (companyId) => {
    setDraftCompanyIds((prev) => {
      const updated = prev.includes(companyId)
        ? prev.filter((id) => id !== companyId)
        : [...prev, companyId];

      setHasChanges(true);
      return updated;
    });
  };

  const handleToggleAll = () => {
    setDraftCompanyIds((prev) => {
      const updated =
        prev.length === allCompanyIds.length
          ? []
          : allCompanyIds;

      setHasChanges(true);
      return updated;
    });
  };

  // ===== APLICAR =====

  const handleApplyChanges = () => {
    sessionStorage.setItem(
      "selectedCompanies",
      JSON.stringify(draftCompanyIds)
    );

    setSelectedCompanyIds(draftCompanyIds);
    setHasChanges(false);

    window.location.reload();
  };

  // ===== FILTRO =====

  const handleFilterChange = (event) =>
    setFilterText(event.target.value);

  const handleKeyDown = (event) => {
    if (["ArrowUp", "ArrowDown", "Enter"].includes(event.key))
      return;
    event.stopPropagation();
  };

  // ===== TEXTO DO BOTÃO =====

  const displayText = () => {
    const fallback = selectPh;
    if (!selectedCompanyIds.length) return fallback;

    const selectedCompanyNames = companies
      .filter((company) => selectedCompanyIds.includes(company.id))
      .map((company) => company.nome);

    // IDs no storage antes da lista /companies hidratar os nomes
    if (selectedCompanyNames.length === 0) {
      return fallback;
    }

    if (selectedCompanyNames.length <= maxDisplayNames) {
      return selectedCompanyNames.join(", ");
    }

    const displayed = selectedCompanyNames
      .slice(0, maxDisplayNames)
      .join(", ");

    const remainingCount =
      selectedCompanyNames.length - maxDisplayNames;

    return `${displayed} +${remainingCount}`;
  };

  // ===== RENDER =====

  return (
    <>
      <Box sx={{ display: "flex", alignItems: "center" }}>
        <Button
          variant="outlined"
          onClick={handleOpenMenu}
          endIcon={<ArrowDropDown />}
          sx={{
            textTransform: "none",
            borderRadius: "8px",
            p: "6px 12px",
            fontSize: "14px",
            width: "100%",
            maxWidth: "300px",
            display: "inline-flex",
            justifyContent: "flex-start",
            backgroundColor: theme.palette.background.default,
            borderColor: theme.palette.divider,
            color: theme.palette.text.primary,
            "& .MuiButton-endIcon": {
              marginLeft: "auto",
              flexShrink: 0,
            },
            "&:hover": {
              backgroundColor: theme.palette.action.hover,
            },
          }}
        >
          <Box
            sx={{
              minWidth: 0,
              flex: "1 1 auto",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              width: "100%",
              maxWidth: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              justifyContent: "center",
            }}
          >
            <Box
              component="span"
              sx={{
                color: theme.palette.text.primary,
                fontWeight: 700,
                fontSize: "0.875rem",
                lineHeight: 1.2,
                minWidth: 0,
                maxWidth: "100%",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {displayText()}
            </Box>

            {!workspaceLabel ? null : (
              <Box
                component="span"
                sx={{
                  mt: 0.25,
                  color: theme.palette.text.disabled,
                  fontWeight: 400,
                  fontSize: "0.75rem",
                  lineHeight: 1.1,
                  minWidth: 0,
                  maxWidth: "100%",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {t("company.workspaceOneLine", { name: workspaceLabel })}
              </Box>
            )}
          </Box>
        </Button>

        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleCloseMenu}
          MenuListProps={{
            disablePadding: true,
            sx: { p: 0 },
          }}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "left",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "left",
          }}
          PaperProps={{
            sx: {
              maxHeight: 420,
              width: 280,
              backgroundColor: theme.palette.background.paper,
              color: theme.palette.text.primary,
              borderRadius: "10px",
              overflowX: "hidden",
              overflowY: "auto",
              boxShadow: theme.shadows[8],
            },
          }}
        >
          {workspaceLabel ? (
            <>
              <Box
                sx={{
                  px: 1.5,
                  py: 1,
                  borderBottom: `1px solid ${theme.palette.divider}`,
                  backgroundColor: theme.palette.action.hover,
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  textAlign: "center",
                }}
              >
                <Typography variant="body2" component="div" sx={{ textAlign: "center" }}>
                  <Box component="span" sx={{ color: theme.palette.text.secondary }}>
                    {t("company.workspacePrefix")}
                  </Box>
                  <Box component="span" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
                    {workspaceLabel}
                  </Box>
                </Typography>
              </Box>
            </>
          ) : null}

          {companies.length > 1 ? (
            <>
              {/* Filtro */}
              <Box sx={{ px: "12px", pt: "8px", pb: "8px" }}>
                <TextField
                  variant="outlined"
                  size="small"
                  fullWidth
                  placeholder={t("company.filterPlaceholder")}
                  value={filterText}
                  onChange={handleFilterChange}
                  onKeyDown={handleKeyDown}
                />
              </Box>

              {/* Selecionar todas */}
              <MenuItem onClick={handleToggleAll}>
                <Checkbox
                  checked={allSelected}
                  indeterminate={someSelected}
                />
                <ListItemText
                  primary={t("company.allCompanies")}
                  primaryTypographyProps={{
                    variant: "body2",
                    fontWeight: 600,
                  }}
                  secondary={
                    companies.length
                      ? t("company.selectedCount", {
                          current: draftCompanyIds.length,
                          total: companies.length,
                        })
                      : undefined
                  }
                  secondaryTypographyProps={{
                    variant: "caption",
                    sx: {
                      mt: 0.15,
                      color: theme.palette.text.disabled,
                      fontWeight: 400,
                      fontSize: "0.7rem",
                      opacity: 0.85,
                    },
                  }}
                />
              </MenuItem>

              <Divider />
            </>
          ) : null}

          {/* Loading */}
          {isLoading && (
            <MenuItem disabled>
              <CircularProgress size={24} />
            </MenuItem>
          )}

          {/* Error */}
          {isError && (
            <MenuItem onClick={refetch}>
              {t("company.loadError")}
            </MenuItem>
          )}

          {/* Lista */}
          {!isLoading &&
            companies.map((company) => (
              <MenuItem
                key={company.id}
                onClick={() =>
                  handleToggleCompany(company.id)
                }
              >
                <Checkbox
                  checked={draftCompanyIds.includes(
                    company.id
                  )}
                />
                <ListItemText
                  primary={company.nome}
                  primaryTypographyProps={{
                    variant: "body2",
                    fontWeight: 600,
                  }}
                  secondary={
                    company.id != null
                      ? t("company.companyIdLine", { id: company.id })
                      : undefined
                  }
                  secondaryTypographyProps={{
                    variant: "caption",
                    sx: {
                      mt: 0.15,
                      color: theme.palette.text.disabled,
                      fontWeight: 400,
                      fontSize: "0.7rem",
                      opacity: 0.75,
                    },
                  }}
                />
              </MenuItem>
            ))}

          {/* Footer fixo */}
          <Box
            sx={{
              position: "sticky",
              bottom: 0,
              p: "8px 12px",
              backgroundColor:
                theme.palette.background.paper,
              borderTop: `1px solid ${theme.palette.divider}`,
              display: "flex",
              justifyContent: "center",
            }}
          >
            {hasChanges && (
              <Button
                variant="contained"
                onClick={handleApplyChanges}
                sx={{
                  textTransform: "none",
                  borderRadius: "8px",
                  px: "16px",
                  fontSize: "14px",
                }}
              >
                {t("company.applyChanges")}
              </Button>
            )}
          </Box>
        </Menu>
      </Box>

      {/* Aviso ao fechar sem aplicar */}
      <Snackbar
        open={showWarning}
        autoHideDuration={4000}
        onClose={() => setShowWarning(false)}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
      >
        <Alert
          severity="warning"
          variant="filled"
          onClose={() => setShowWarning(false)}
        >
          {t("company.unsavedChanges")}
        </Alert>
      </Snackbar>
    </>
  );
};

export default CompanySelector;
